import { numberFormatter } from 'core/helpers/formatters';
import { mineSubsidenceOptional, getValue, UNLIMITED_D_VALUE, dayCareStates } from '@ourbranch/lookups';

export const getShowMineSubsidence = (offer) => {
  const notBuyingHome = !offer?.quote?.selectedOption?.includes('H');
  if (!offer || !offer?.quote?.home || notBuyingHome) {
    return false;
  }

  const { home, correctedAddress } = offer.quote;

  return (
    home.county &&
    mineSubsidenceOptional[correctedAddress.state] &&
    (!!mineSubsidenceOptional[correctedAddress.state][home.county.toUpperCase()] ||
      !!mineSubsidenceOptional[correctedAddress.state].all)
  );
};

export const getShowDayCare = (state) => dayCareStates.includes(state);

export const getShowMSALE = (state) => state === 'IN';

export const getShowSinkhole = (state) => state === 'TN';

// currently not offering Oil Storage Tank Coverage
export const getShowOilStorageTank = () => false;

const getShowMSALEFromQuote = (offer) => {
  if (!offer) {
    return false;
  }

  const {
    correctedAddress: { state }
  } = offer.quote;

  return getShowMineSubsidence(offer) && getShowMSALE(state);
};

const money = (value) => `$${numberFormatter(value, 2)}`;
const percentage = (value) => `${value}%`;
const getPercentage = (value, perc) => (value * perc) / 100;
const boolCoverage = (value, hideIfFalse) => {
  if (value) {
    return 'Covered';
  }
  return hideIfFalse ? null : 'No Coverage';
};
// 0% ALE reads "Not Purchased", matching quote-fe, policy documents and account-fe (#37212)
const additionalLivingExpense = ({ coverageA, coverageDPctOfA }) => {
  if (coverageDPctOfA === UNLIMITED_D_VALUE) {
    return 'UNLTD';
  }
  if (coverageDPctOfA === 0) {
    return 'Not Purchased';
  }
  return money(getPercentage(coverageA, coverageDPctOfA));
};

export const getCoverage = (offer) => {
  const hideMineSubsidence = !getShowMineSubsidence(offer);
  const hideMSALE = !getShowMSALEFromQuote(offer);

  if (!offer) {
    return {};
  }

  const {
    umbrellaCoverage,
    scheduledPersonalProperty,
    homeCoverage,
    autoCoverage,
    cars,
    rentersCoverage,
    correctedAddress,
    includeUmbrella
  } = offer.quote;
  const hasJewelryCoverage =
    scheduledPersonalProperty &&
    Array.isArray(scheduledPersonalProperty.items) &&
    scheduledPersonalProperty.items.length;
  // A mono-line quote (e.g. CFM auto) only carries the blocks for the products it offers — no
  // homeCoverage / rentersCoverage / autoCoverage. Each section below is omitted when its block is
  // absent instead of dereferencing it, so a caller that only wants the top-level flags (checkout's
  // CancelCurrentPolicy) doesn't crash the page (#36206).
  const [personalProperty, livingExpense] = rentersCoverage?.coverageCD?.split('/') ?? [];

  return {
    hasUmbrellaCoverage: includeUmbrella,
    hasJewelryCoverage,
    homeCoverage: homeCoverage && {
      dwelling: money(homeCoverage.coverageA),
      incrementalDwelling: percentage(homeCoverage.coverageIBSC),
      otherStructuresLimit: money(getPercentage(homeCoverage.coverageA, homeCoverage.coverageBPctOfA)),
      waterBackup: getValue('waterBackupCoverageOptions', homeCoverage.coverageWaterBackup, correctedAddress.state),
      additionalLivingExpense: additionalLivingExpense(homeCoverage),
      homeSharing: boolCoverage(homeCoverage.homeownerProtection),
      roofSurfacesExtended: boolCoverage(homeCoverage.coverageRSExtended),
      coverageMS: boolCoverage(homeCoverage.coverageMS, hideMineSubsidence),
      coverageMSALE: boolCoverage(homeCoverage.coverageMSALE, hideMSALE),
      guestMedicalLimit: money(homeCoverage.coverageY),
      personalPropertyLimit: money(getPercentage(homeCoverage.coverageA, homeCoverage.coverageCPctOfA)),
      liabilityLimit: money(homeCoverage.coverageX),
      deductibleWindHail:
        homeCoverage.deductibleWindHail < 1
          ? `${percentage(homeCoverage.deductibleWindHail * 100)} (${money(
              homeCoverage.deductibleWindHail * homeCoverage.coverageA
            )})`
          : money(homeCoverage.deductibleWindHail),
      deductibleAllOther:
        homeCoverage.deductibleAllOther < 1
          ? `${percentage(homeCoverage.deductibleAllOther * 100)} (${money(
              homeCoverage.deductibleAllOther * homeCoverage.coverageA
            )})`
          : money(homeCoverage.deductibleAllOther),
      interestedInSeparateWindHail: boolCoverage(homeCoverage.interestedInSeparateWindHail)
    },
    autoCoverage: autoCoverage && {
      policyLimitBIPD: getValue('policyLimitBIPD', autoCoverage.policyLimitBIPD),
      policyLimitMedicalPayments: getValue('policyLimitMedicalPayments', autoCoverage.policyLimitMedicalPayments),
      policyLimitUMBI: getValue('policyLimitUMBI', autoCoverage.policyLimitUMBI)
    },
    carsCoverages: (cars ?? []).reduce((obj, car) => {
      obj[car.VIN] = {
        deductibleCollision: getValue('deductibleCollision', car.deductibleCollision),
        deductibleComprehensive: getValue('deductibleComprehensive', car.deductibleComprehensive),
        limitRental: getValue('limitRental', car.limitRental),
        roadsideAssistance: boolCoverage(car.roadsideAssistance),
        limitUMPD: getValue('limitUMPD', car.limitUMPD),
        rideSharing: boolCoverage(car.rideSharing),
        coverageLoan: boolCoverage(car.coverageLoan)
      };
      return obj;
    }, {}),
    scheduledPersonalProperty: {
      deductible: hasJewelryCoverage ? money(scheduledPersonalProperty.deductible) : 'No Coverage',
      items:
        scheduledPersonalProperty && Array.isArray(scheduledPersonalProperty.items)
          ? scheduledPersonalProperty.items.map((item) => {
              return {
                ...item,
                valueFormatted: money(item.value)
              };
            })
          : []
    },
    umbrellaCoverage: {
      liabilityLimit:
        includeUmbrella && umbrellaCoverage ? money(umbrellaCoverage.liabilityCoverageLimit) : 'No Coverage'
    },
    rentersCoverage: rentersCoverage && {
      liabilityLimit: money(rentersCoverage.coverageX),
      deductible: money(rentersCoverage.deductible),
      guestMedicalLimit: money(rentersCoverage.coverageY),
      personalProperty: money(parseFloat(personalProperty)),
      livingExpense: money(parseFloat(livingExpense))
    }
  };
};
